import { API_CONFIG } from '../config/api';
import { store } from '../stores/store';  // Import your Redux store

/**
 * Generic function to fetch data from the API
 * @param {string} endpoint - The API endpoint to fetch from
 * @param {Object} options - Additional options for the fetch call
 * @returns {Promise<{data: any, status: number}>} - A promise that resolves to the response data and status
 * @throws {Error} - Throws an error if the API call fails
 */
const fetchData = async (endpoint, options = {}) => {
    // Get token from Redux state
    const token = store.getState().auth.token;

    console.log('Making API request:', {
        endpoint,
        hasToken: !!token,
        method: options.method || 'GET'
    });

    try {
        // Merge headers with auth token if it exists
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }), // Always add token if it exists
            ...options.headers, // Allow overriding headers if needed
        };

        console.log('Request headers:', Object.keys(headers));

        const response = await fetch(`${API_CONFIG.BASE_URL}/${endpoint}`, {
            headers,
            method: options.method || 'GET',
            body: options.body ? JSON.stringify(options.body) : undefined,
        });

        // Handle different types of errors
        if (!response.ok) {
            let errorData;
            try {
                // Try to parse the error response as JSON
                errorData = await response.json();
                
                // Create a custom error object with the response data
                const error = new Error(errorData.message || `API error: ${response.status}`);
                error.response = { data: errorData, status: response.status };
                
                // Handle authentication errors
                if (response.status === 401 && token) {
                    store.dispatch({ type: 'auth/logout' });
                }
                
                throw error;
            } catch (e) {
                // If response is not JSON or another error occurred
                if (e.response) {
                    throw e; // This is our custom error with response data
                }
                // Otherwise create a simple error
                const error = new Error(`API error: ${response.status} ${response.statusText}`);
                error.status = response.status;
                throw error;
            }
        }

        // Handle empty responses
        if (response.status === 204) {
            return { data: null, status: response.status };
        }

        // Parse JSON response
        const responseData = await response.json();
        const data = responseData.data || responseData;
        return { data, status: response.status };
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
};

/**
 * Fetch all estates
 * @returns {Promise<{data: Array, status: number}>} - A promise that resolves to an array of estates and the response status
 */
export const fetchAllEstates = async () => {
    return await fetchData('estates');
};

/**
 * Fetch a single estate by ID
 * @param {number|string} id - The ID of the estate to fetch
 * @returns {Promise<{data: Object, status: number}>} - A promise that resolves to the estate object and the response status
 */
export const fetchEstateById = async (id) => {
    return await fetchData(`estates/${id}`);
};

/**
 * Fetch all regions
 * @returns {Promise<{data: Array, status: number}>} - A promise that resolves to an array of regions and the response status
 */
export const fetchAllRegions = async () => {
    return await fetchData('regions');
};

export const fetchSubRegionsByParentId = async (id) => {
    return await fetchData(`regions/${id}/subregions`);
};

/**
 * Fetch a single region by ID
 * @param {number|string} id - The ID of the region to fetch
 * @returns {Promise<{data: Object, status: number}>} - A promise that resolves to the region object and the response status
 */
export const fetchRegionById = async (id) => {
    return await fetchData(`regions/${id}`);
};

export const logoutUser = async () => {
    try {
        await fetchData('logout', {
            method: 'POST'
        });
    } finally {
        // Always clear token on logout, even if the API call fails
        localStorage.removeItem('token');
    }
};

export const loginUser = async (credentials) => {
    const response = await fetchData('login', {
        method: 'POST',
        body: credentials
    });

    // Save token after successful login
    if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
    }

    return response;
};

export const registerUser = async (userData) => {
    return await fetchData('register', {
        method: 'POST',
        body: userData
    });
};

export const saveUserSearch = async (searchData) => {
    return await fetchData('search/store', {
        method: 'POST',
        body: searchData,
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

export const fetchUserSearches = async () => {
    return await fetchData('search/index', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

export const fetchReviews = async () => {
    return await fetchData('reviews', {});
};

export const fetchUserFavorites = async () => {
    return await fetchData('favorites', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

export const toggleFavorite = async (estateId) => {
    return await fetchData(`favorites/${estateId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

/**
 * Fetch all favorite estates for the authenticated user
 * @returns {Promise<{data: Array, status: number}>} - A promise that resolves to an array of favorite estates
 */
export const fetchFavoriteEstates = async () => {
    return await fetchData('favourite-estates', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

/**
 * Add an estate to user's favorites
 * @param {number|string} estateId - The ID of the estate to favorite
 * @returns {Promise<{data: Object, status: number}>} - A promise that resolves to the response data
 */
export const addFavoriteEstate = async (estateId) => {
    return await fetchData(`favourite-estates/${estateId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

/**
 * Remove an estate from user's favorites
 * @param {number|string} favoriteEstateId - The ID of the favorite estate record to remove
 * @returns {Promise<{data: Object, status: number}>} - A promise that resolves to the response data
 */
export const removeFavoriteEstate = async (favoriteId) => {
    return await fetchData(`favourite-estates/${favoriteId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

/**
 * Submit contact form data
 * @param {Object} contactData - The contact form data
 * @param {string} contactData.name - The name of the person
 * @param {string} contactData.email - The email address
 * @param {string} contactData.phone - The phone number (optional)
 * @param {string} contactData.message - The message content
 * @returns {Promise<{data: Object, status: number}>} - A promise that resolves to the response data
 */
export const submitContactForm = async (contactData) => {
    return await fetchData('contact', {
        method: 'POST',
        body: contactData
    });
};